<template>
  <div>
    <Breadcrumbs title="Subscriptions" />

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">
                  <i class="fas fa-credit-card me-2"></i>
                  Subscription Management
                </h4>
                <div class="d-flex gap-2">
                  <button 
                    class="btn btn-primary" 
                    @click="openCreateModal"
                    :disabled="!canCreate('subscriptions')"
                  >
                    <i class="fas fa-plus me-1"></i>
                    Add Subscription
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body">
              <!-- Filters and Search -->
              <div class="row mb-4 justify-content-between">
                <div class="col-md-3">
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                    <input
                      v-model="searchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Search subscriptions..."
                      @input="handleSearch"
                    />
                  </div>
                </div>
                <div class="col-md-5">
                  <div class="d-flex gap-2"> 
                  <input
                    width="200px"
                    v-model="filterUserId"
                    type="text"
                    class="form-control"
                    placeholder="Filter by User ID"
                    @input="handleFilter"
                  />
                   <select
                   style="width: 150px;"
                    v-model="sortBy"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="createdAt">Created Date</option>
                    <option value="updatedAt">Updated Date</option>
                    <option value="status">Status</option>
                    <option value="userId">User ID</option>
                  </select>
                  </div>
                </div>
                 
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Subscriptions Table -->
              <div
                v-else-if="subscriptions.length > 0"
                class="table-responsive table_style"
              >
                <table class="table table-striped table-hover align-middle ">
                  <thead class="table-dark">
                    <tr>
                      <th scope="col" style="width: 80px">Sr No</th>
                      <th scope="col" style="min-width: 120px">User ID</th>
                      <th scope="col" style="min-width: 150px">Package</th>
                      <th scope="col" style="width: 100px">Status</th>
                      <th scope="col" style="width: 100px">Interval</th>
                      <th scope="col" style="width: 120px">Price</th>
                      <th scope="col" style="width: 160px">Trial Days</th>
                      <th scope="col" style="width: 120px">Created</th>
                      <th scope="col" style="width: 200px">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="table-group-divider">
                    <tr
                      v-for="(subscription, index) in subscriptions"
                      :key="subscription._id"
                    >
                      <td class="text-center">
                        <span class="badge bg-secondary">
                          {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                        </span>
                      </td>
                      <td>
                        <div>
                          <strong>{{
                            getUserDisplay(subscription.userId)
                          }}</strong>
                          <!-- <div class="text-muted small">
                            {{ subscription.userId._id }}
                          </div> -->
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{{
                            getPackageName(subscription.packageId)
                          }}</strong>
                          <div
                            v-if="subscription.workspaceId"
                            class="text-muted small"
                          >
                            Workspace: {{ subscription.workspaceId }}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          :class="getStatusBadgeClass(subscription.status)"
                          class="badge"
                        >
                          {{ formatStatus(subscription.status) }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-info">
                          {{ formatInterval(subscription.interval) }}
                        </span>
                      </td>
                      <td>
                        <div v-if="subscription.customPrice">
                          <strong>{{
                            formatAmount(
                              subscription.customPrice.amount,
                              subscription.customPrice.currency
                            )
                          }}</strong>
                          <div class="text-muted small">Custom</div>
                        </div>
                        <div v-else-if="subscription.packagePrice">
                          <strong>{{
                            formatAmount(
                              subscription.packagePrice.amount,
                              subscription.packagePrice.currency
                            )
                          }}</strong>
                          <div class="text-muted small">Package</div>
                        </div>
                        <div v-else>
                          <span class="text-muted">N/A</span>
                        </div>
                      </td>
                      <td class="text-center">
                        <span
                          v-if="subscription.trialDays"
                          class="badge bg-warning text-dark"
                        >
                          {{ subscription.trialDays }} days
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span class="text-muted small">
                          {{ formatDate(subscription.createdAt) }}
                        </span>
                      </td>
                      <td>
                        <div class="d-flex gap-1">
                          <button
                            class="btn btn-sm btn-outline-info"
                            @click="viewSubscription(subscription)"
                            title="View Details"
                            :disabled="!canView('subscriptions')"
                          >
                            <i class="fas fa-eye"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click="editSubscription(subscription)"
                            title="Edit"
                            :disabled="!canUpdate('subscriptions')"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <div class="dropdown">
                            <button
                              class="btn btn-sm text-center border btn-outline-secondary dropdown-toggle"
                              type="button"
                              :id="`dropdown-${subscription._id}`"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              title="More Actions"
                              :disabled="!canUpdate('subscriptions')"
                            >
                              <i class="fas fa-ellipsis-h"></i>
                            </button>
                            <ul
                              class="dropdown-menu"
                              :aria-labelledby="`dropdown-${subscription._id}`"
                            >
                              <li v-if="subscription.status === 'active'">
                                <a
                                  class="dropdown-item"
                                  href="#"
                                  @click.prevent="
                                    pauseSubscription(subscription._id)
                                  "
                                >
                                  <i class="fas fa-pause me-2"></i>Pause
                                </a>
                              </li>
                              <li v-if="subscription.status === 'paused'">
                                <a
                                  class="dropdown-item"
                                  href="#"
                                  @click.prevent="
                                    resumeSubscription(subscription._id)
                                  "
                                >
                                  <i class="fas fa-play me-2"></i>Resume
                                </a>
                              </li>
                              <li
                                v-if="
                                  ['active', 'trialing', 'past_due'].includes(
                                    subscription.status
                                  )
                                "
                              >
                                <a
                                  class="dropdown-item text-danger"
                                  href="#"
                                  @click.prevent="
                                    cancelSubscription(subscription._id)
                                  "
                                >
                                  <i class="fas fa-times me-2"></i>Cancel
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <i class="fas fa-credit-card fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No subscriptions found</h5>
                <p class="text-muted">
                  Create your first subscription to get started.
                </p>
                <button class="btn btn-primary" @click="openCreateModal">
                  <i class="fas fa-plus me-1"></i>
                  Add Subscription
                </button>
              </div>

              <!-- Pagination -->
              <div
                v-if="totalPages > 1"
                class="d-flex justify-content-between align-items-center mt-4"
              >
                <div class="text-muted">
                  Showing {{ startItem }} to {{ endItem }} of {{ totalItems }}
                  subscriptions
                </div>
                <nav aria-label="Subscriptions pagination">
                  <ul class="pagination mb-0">
                    <li
                      class="page-item"
                     :class="{ disabled: currentPage === 1 }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="changePage(currentPage - 1)"
                        aria-label="Previous"
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </a>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentPage }"

                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="typeof page === 'number' && changePage(page)"
                        :class="{'text-white': page === currentPage }"
                      >
                        {{ page }}
                      </a>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage === totalPages }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="changePage(currentPage + 1)"
                        aria-label="Next"
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <SubscriptionModal
      v-if="showModal"
      :show="showModal"
      :subscription="selectedSubscription"
      :is-edit="isEditMode"
      :packages="packages"
      :currencies="currencies"
      @close="closeModal"
      @saved="handleSubscriptionSaved"
    />

    <!-- View Modal -->
    <SubscriptionViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :subscriptionData="selectedSubscription"
      :packages="packages"
      :currencies="currencies"
      @close="closeViewModal"
      @edit="handleEditFromView"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canUpdate, canCreate } = usePermissions();

// Lazy load modal components
const SubscriptionModal = defineAsyncComponent(
  () => import("@/components/theme/admin/subscriptions/SubscriptionModal.vue")
);
const SubscriptionViewModal = defineAsyncComponent(
  () =>
    import("@/components/theme/admin/subscriptions/SubscriptionViewModal.vue")
);

// Toast functionality is imported above

// Reactive data
const subscriptions = ref<any[]>([]);
const packages = ref<any[]>([]);
const currencies = ref<any[]>([]);
const loading = ref(false);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);

// Search and filters
const searchQuery = ref("");  
const filterUserId = ref("");
const sortBy = ref("createdAt");

// Modal states
const showModal = ref(false);
const showViewModal = ref(false);
const selectedSubscription = ref<any>(null);
const isEditMode = ref(false);

// Debounced search
let searchTimeout: any;

// Computed
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const startItem = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);

const endItem = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
);

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const delta = 2;

  const range = [];
  const rangeWithDots = [];

  for (
    let i = Math.max(2, current - delta);
    i <= Math.min(total - 1, current + delta);
    i++
  ) {
    range.push(i);
  }

  if (current - delta > 2) {
    rangeWithDots.push(1, "...");
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (current + delta < total - 1) {
    rangeWithDots.push("...", total);
  } else if (total > 1) {
    rangeWithDots.push(total);
  }

  return rangeWithDots.filter(
    (item, index, array) => array.indexOf(item) === index
  );
});
const fetchPackages = async () => {
  try {
    const response = await apiService.getPackages({ limit: 100 });
    if (response?.packages) {
      packages.value = response.packages;
    } else if (response?.data?.packages) {
      packages.value = response.data.packages;
    } else {
      packages.value = [];
    }
  } catch (error) {
    console.error("Error fetching packages:", error);
  }
};

// Methods
const fetchSubscriptions = async () => {
  try {
    loading.value = true; 
    const params: any = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      type: 'free'
    };


    if (searchQuery.value) {
      params.search = searchQuery.value;
    } 

    if (filterUserId.value) {
      params.userId = filterUserId.value;
    }

    const response = await apiService.getSubscriptions(params);

    // Handle the response structure
    if (response?.subscriptions) {
      subscriptions.value = response.subscriptions;
      totalItems.value =
        response.pagination?.totalItems || response.subscriptions.length;
    } else if (response?.data?.subscriptions) {
      subscriptions.value = response.data.subscriptions;
      totalItems.value =
        response.data.pagination?.totalItems ||
        response.data.subscriptions.length;
    } else if (Array.isArray(response)) {
      subscriptions.value = response;
      totalItems.value = response.length;
    } else {
      subscriptions.value = [];
      totalItems.value = 0;
    }
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    toast.error("Failed to load subscriptions");
    subscriptions.value = [];
  } finally {
    loading.value = false;
  }
};



const fetchCurrencies = async () => {
  try {
    const response = await apiService.getCurrencies({ limit: 100 });
    if (response.data && response.data.currencies) {
      currencies.value = response.data.currencies;
    } else if (response.currencies) {
      currencies.value = response.currencies;
    } else {
      currencies.value = response || [];
    }
  } catch (error) {
    console.error("Error fetching currencies:", error);
  }
};

const handleSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
    fetchSubscriptions();
  }, 500);
};

const handleFilter = () => {
  currentPage.value = 1;
  fetchSubscriptions();
};

const resetFilters = () => {
  searchQuery.value = ""; 
  filterUserId.value = "";
  sortBy.value = "createdAt";
  currentPage.value = 1;
  fetchSubscriptions();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchSubscriptions();
  }
};

// Modal methods
const openCreateModal = () => {
  selectedSubscription.value = null;
  isEditMode.value = false;
  showModal.value = true;
};

const editSubscription = (subscription: any) => {
  selectedSubscription.value = { ...subscription };
  isEditMode.value = true;
  showModal.value = true;
};

const viewSubscription = (subscription: any) => {
  selectedSubscription.value = subscription;
  showViewModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedSubscription.value = null;
  isEditMode.value = false;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedSubscription.value = null;
};

const handleEditFromView = (subscription: any) => {
  closeViewModal();
  editSubscription(subscription);
};

const handleSubscriptionSaved = () => {
  closeModal();
  fetchSubscriptions();
  toast.success(
    isEditMode.value
      ? "Subscription updated successfully"
      : "Subscription created successfully"
  );
};

// Subscription actions
const pauseSubscription = async (id: string) => {
  if (!confirm("Are you sure you want to pause this subscription?")) return;

  try {
    await apiService.pauseSubscription(id);
    toast.success("Subscription paused successfully");
    fetchSubscriptions();
  } catch (error) {
    console.error("Error pausing subscription:", error);
    toast.error("Failed to pause subscription");
  }
};

const resumeSubscription = async (id: string) => {
  if (!confirm("Are you sure you want to resume this subscription?")) return;

  try {
    await apiService.resumeSubscription(id);
    toast.success("Subscription resumed successfully");
    fetchSubscriptions();
  } catch (error) {
    console.error("Error resuming subscription:", error);
    toast.error("Failed to resume subscription");
  }
};

const cancelSubscription = async (id: string) => {
  if (
    !confirm(
      "Are you sure you want to cancel this subscription? This action cannot be undone."
    )
  )
    return;

  try {
    await apiService.cancelSubscription(id);
    toast.success("Subscription canceled successfully");
    fetchSubscriptions();
  } catch (error) {
    console.error("Error canceling subscription:", error);
    toast.error("Failed to cancel subscription");
  }
};

// Utility methods
const getUserDisplay = (userId: any): string => {
  if (typeof userId === "object" && userId !== null) {
    return userId.name || userId.email || userId._id || "Unknown User";
  }
  return userId || "Unknown User";
};

const getPackageName = (packageData: any): string => {
  if (typeof packageData === "object" && packageData !== null) {
    return packageData.name || "Unknown Package";
  } else if (typeof packageData === "string") {
    const foundPackage = packages.value.find((p) => p._id === packageData);
    return foundPackage?.name || "Unknown Package";
  }
  return "Unknown Package";
};

const getStatusBadgeClass = (status: string): string => {
  const statusClasses: Record<string, string> = {
    active: "bg-success text-white",
    trialing: "bg-info text-white",
    past_due: "bg-warning text-dark",
    canceled: "bg-danger text-white",
    unpaid: "bg-secondary text-white",
    paused: "bg-dark text-white",
  };
  return statusClasses[status] || "bg-secondary text-white";
};

const formatStatus = (status: string): string => {
  const statusLabels: Record<string, string> = {
    active: "Active",
    trialing: "Trialing",
    past_due: "Past Due",
    canceled: "Canceled",
    unpaid: "Unpaid",
    paused: "Paused",
  };
  return statusLabels[status] || status;
};

const formatInterval = (interval: string): string => {
  const intervals: Record<string, string> = {
    day: "Daily",
    week: "Weekly",
    month: "Monthly",
    year: "Yearly",
  };
  return intervals[interval] || interval;
};

const formatAmount = (amount: number, currency: any): string => {
  const currencyCode = typeof currency === "object" ? currency.code : currency;
  const currencySymbol = typeof currency === "object" ? currency.symbol : "$";
  return `${currencySymbol}${(amount / 100).toFixed(2)}`;
};

const formatDate = (date: string): string => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

onMounted(async () => {
  await Promise.all([fetchPackages(), fetchCurrencies(), fetchSubscriptions()]);
});
</script>

<style scoped>
.table-responsive {
  border-radius: 0.5rem;
  overflow: hidden;
}

.badge {
  font-size: 0.75em;
}

.dropdown-toggle::after {
  display: none;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
}

.table-group-divider {
  border-top: 2px solid #dee2e6;
}

.btn-group .btn {
  margin: 0;
}

.pagination .page-link {
  color: #6c757d;
}

.pagination .page-item.active .page-link {
  background-color: #35bfbf;
  border-color: #35bfbf;
  outline: none;
}
.pagination .page-item.active .page-link:focus{
  outline: none;
  border: 0;
}
.table_style table{
  min-width: 1400px;
}
.table_style{
  overflow-x:auto !important;
}

.dropdown .dropdown-toggle{
  border-radius: 4px !important;
}
</style>
